import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, HostListener, inject, Input, NgZone, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { SelectionMode, TableAction, TableActionType, TableConfig, TableEvent, TableField, TableSummaryData } from 'src/app/core/models/table-config';
import { RowSelect, selectCurrentRow, selectFeatureCode, State } from 'src/app/store/shared/shared.reducers';
import { debounceTime, distinctUntilChanged, map, merge, Observable, Subject, take } from 'rxjs';
import { HostColumnEditService } from 'src/app/shared/services/host-column-edit.service';
import { environment } from 'src/environments/environment';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTable } from '@angular/material/table';
import { Sort, SortDirection } from '../../../models/dynacrud';
import { SortArrowDirection, directionFromPrioritisedSort, prioritisedSortFromEcho, secondarySortDirection, secondarySortRank } from 'src/app/shared/utils/sort-direction';
import { selectAuth } from 'src/app/store/auth/auth.reducers';
import { Auth } from 'src/app/auth/models/auth';
import { isFunction, isObject } from 'lodash';
import { select, Store } from '@ngrx/store';
import { SubSink } from 'subsink';
import * as _ from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { DialogRowComponent } from '../dialogs/dialog-row/dialog-row.component';
import { strategySelect } from 'src/app/features/warehouse/components/bodies/all-sessions-into-wrap/all-sessions-into-wrap.component';
import { Motivation } from 'src/app/features/warehouse/models/entities';
import { DefaultLayoutService } from 'src/app/layout/services/default-layout.service';
import { TranslateService } from '@ngx-translate/core';
import { ScrollToTopService } from 'src/app/shared/services/scroll-to-top.service';


export type Roles = Pick<Auth, "roles">;


// https://stackblitz.com/angular/eglxlnboper?file=src%2Fapp%2Fad-banner.component.ts

export interface TableStatus {
  page: PageEvent;
  sort: Sort[];
}

export interface TableData {
  data: [];
  // potremmo avere già search ecc...
  tableStatus: TableStatus;
}

interface SortItem {
  field: TableField;
  position: number;
  direction: SortDirection;
}

interface SortStatus {
  [id: string]: SortItem;
}
export interface TableSelection {
  all?: boolean;
  rows: any[];
  code?: string;
  ctrlKey?: boolean;
}

export enum customPaginatiorMode {
  customSelectPage = 'CUSTOMSELECTPAGE'
}

/**
 * The table's name while rows are navigable, by what Enter does on them. A map rather than a key
 * built from parts, so `audit:i18n` resolves every one of them.
 */
// g15-i18n-keys
export const ROW_NAVIGATION_HINT_KEYS = {
  open: 'common.table.rowNavigationHint',
  pick: 'common.table.rowNavigationHintPick',
  expand: 'common.table.rowNavigationHintExpand',
  mixed: 'common.table.rowNavigationHintMixed',
  mixedPick: 'common.table.rowNavigationHintMixedPick',
};

/** A group row's state, read by a screen reader from its first value cell. */
// g15-i18n-keys
export const GROUP_STATE_KEYS = {
  expanded: 'common.table.groupExpanded',
  collapsed: 'common.table.groupCollapsed',
};

/** Which hint names a table whose navigable rows answer Enter with these outcomes. */
export function rowNavigationHintFor(outcomes: Set<string>): string | null {
  if (!outcomes.size) {
    return null;
  }
  if (outcomes.has('expand')) {
    if (outcomes.size === 1) {
      return ROW_NAVIGATION_HINT_KEYS.expand;
    }
    // Groups beside pickers: Enter chooses a row, it opens nothing, so the open wording would lie.
    return outcomes.has('open') ? ROW_NAVIGATION_HINT_KEYS.mixed : ROW_NAVIGATION_HINT_KEYS.mixedPick;
  }
  return outcomes.has('open') ? ROW_NAVIGATION_HINT_KEYS.open : ROW_NAVIGATION_HINT_KEYS.pick;
}

/**
 * The element that scrolls `el`: its nearest ancestor with a scrollable overflow that actually
 * overflows, or the document's scrolling element. What a sticky row sticks to.
 */
function scrollContainerOf(el: HTMLElement): Element {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const overflowY = getComputedStyle(p).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && p.scrollHeight > p.clientHeight) {
      return p;
    }
  }
  return document.scrollingElement ?? document.documentElement;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  featureCode: string | undefined = '';
  statusPer!: RowSelect;
  customPaginatiorModev = customPaginatiorMode;
  modeIcon = false;
  setAvailable = false;
  private static largeViewportWidth = 992;
  private $displayedColumns!: string[] | undefined;
  selection: TableSelection = { all: false, rows: [] };
  tableData: TableData = { data: [], tableStatus: { page: { pageIndex: 0, pageSize: 0, length: 0 }, sort: [] } };
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;
  /**
   * The `#table` template reference on `<mat-table>` -- `static: false` because the table only
   * renders once `tableData`/`config.columnsFields` resolve (see the `*ngIf` on `.table-container`
   * in the template). Used solely to call `renderRows()` when `config.groupRow.changes$` fires --
   * see that field's own comment in `table-config.ts` for the CDK gap this closes.
   */
  @ViewChild('table', { static: false }) matTable?: MatTable<any>;
  /** The `<mat-table>` element itself: its own data rows are its direct `mat-row` children. */
  @ViewChild('table', { static: false, read: ElementRef }) private tableEl?: ElementRef<HTMLElement>;
  selectMode = SelectionMode.single;
  whHost: any[] = [];
  private subs = new SubSink();
  sortStatus: { [id: string]: SortItem } = {};
  private sortSubject: Subject<SortStatus> = new Subject<SortStatus>();
  readonly sort: Observable<SortStatus> = this.sortSubject.asObservable();
  typeObjectId: { [key: string]: any } = {};
  private selectionStatus: { [prop: string]: { row: any, selected: boolean | undefined, not?: boolean, ctrlKey?: boolean } } = {};
  private actions: { [key: string]: TableAction[] } = {};

  @Output()
  rowSelect = new EventEmitter<TableSelection>();

  @Output()
  status = new EventEmitter<TableStatus>(true);

  @Output()
  newPageSingle = new EventEmitter<TableSelection>();




  @Output()
  custom = new EventEmitter<TableEvent>();

  @Input()
  dataSource!: Observable<TableData>;

  @Input()
  notInputPage!: boolean;

  @Input()
  getRowTable: boolean | undefined;

  @Input()
  modelPaginatior: customPaginatiorMode | undefined;

  @Input() strategySelect: strategySelect | undefined;

  valuePagueIndex = 1;
  sumValue: { [k: string]: any } = {};
  sumOwn = 0;
  notPosible: { [k: string]: any } = {};
  fixtureSum = 0;
  private roles: Roles['roles'] = [];
  @ViewChild('inputSingle_') inputSingle_: any;


  @Input()
  config!: TableConfig | undefined;

  definitionValid: string | undefined;

  /** Latest totals for the opt-in sum row (null while loading or when there is nothing to show). */
  summaryData: TableSummaryData | null = null;

  // THE PAGINATOR FOOTER IS PINNED TO THE VIEWPORT, SO IT HAS TO BE SIZED TO THIS COLUMN BY HAND.
  //
  // `.cust_` is `position: fixed` and must stay that way -- WebKit does not repaint a STUCK
  // `position: sticky` layer when only its text changes, which is what made Safari show a stale
  // paginator total (read the long comment on `.cust_` in the SCSS before touching either).
  // But `fixed` resolves against the VIEWPORT: with the SCSS's `left: 0; right: 0` the bar spanned
  // the whole window, ignoring that `mat-sidenav-content` is narrowed by the shop layout's 466px
  // cart panel on /app/shop/cart and pushed right by the sidenav. The page-size select and the
  // "go to page" field ended up underneath the panel -- covered and unreachable.
  //
  // Nothing in CSS can express "viewport-pinned vertically, content-column-wide horizontally":
  // giving an ancestor a `transform`/`contain` to make it the containing block would turn the bar
  // into an absolutely positioned box inside a scroll container, i.e. it would scroll away. So the
  // geometry is mirrored from `.table-container` -- this table's own in-flow box, which every
  // layout already sizes correctly -- instead of hardcoding each layout's panel widths here.
  // Injected with `inject()` rather than as a constructor parameter: several components extend
  // TableComponent and call `super(...)`, so a new constructor argument would be a breaking change
  // across all of them for a dependency only this block uses.
  private readonly zone = inject(NgZone);
  private footerResizeObserver?: ResizeObserver;
  private tableContainerEl?: HTMLElement;

  @ViewChild('tableContainer') set tableContainer(ref: ElementRef<HTMLElement> | undefined) {
    // The container is `*ngIf`-ed on the data, so this fires with `undefined` too -- and again with
    // a NEW element every time the table re-appears. Re-wire rather than assume one lifetime.
    this.observeTableContainer(ref?.nativeElement);
  }

  private observeTableContainer(el: HTMLElement | undefined): void {
    if (el === this.tableContainerEl) {
      return;
    }
    this.footerResizeObserver?.disconnect();
    this.footerResizeObserver = undefined;
    this.tableContainerEl = el;
    if (!el) {
      return;
    }

    this.syncFooterToContent();

    // Guarded: ResizeObserver is missing on Safari < 13.1, and the SCSS fallback (`left: 0;
    // right: 0`) still renders a usable, if full-width, bar there.
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    // Outside the zone: this fires on every frame of the sidenav open/close animation and the
    // callback only writes inline styles on an out-of-flow element -- no change detection is owed,
    // and running one per frame would be a change-detection storm over the whole list.
    this.zone.runOutsideAngular(() => {
      this.footerResizeObserver = new ResizeObserver(() => this.syncFooterToContent());
      this.footerResizeObserver.observe(el);
    });
  }

  /**
   * Lay the fixed paginator footer over this table's own column. Public so a spec can drive it
   * without waiting on a ResizeObserver frame.
   */
  syncFooterToContent(): void {
    const container = this.tableContainerEl;
    const footer = container?.querySelector('.cust_') as HTMLElement | null;
    if (!container || !footer) {
      return;
    }
    const box = container.getBoundingClientRect();
    footer.style.left = `${box.left}px`;
    footer.style.width = `${box.width}px`;
    // `right` is set in the SCSS as the no-JS fallback; with an explicit width it would over-constrain
    // the box, so it has to be released here.
    footer.style.right = 'auto';
  }

  // A resize can move the column without changing its size (the panel and the sidenav both take a
  // share of it), and ResizeObserver only speaks to size.
  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncFooterToContent();
  }

  constructor(public store: Store<State>, private hostColumnEditService: HostColumnEditService,
    private dialogService: DialogService, public dialog: MatDialog, private defaultLayoutService: DefaultLayoutService,
    private translateService: TranslateService, public host: ElementRef<HTMLElement>,
    private scrollToTop: ScrollToTopService) { }

  modifyModeIcon() {
    const v = this.modeIcon = !this.modeIcon;
    this.hostColumnEditService.actualizateMode(v);
  }

  disableForSession(): boolean {

    if (this.featureCode == 'session') {
      const miArray: any[] = _.cloneDeep(this.tableData.data || []);

      if (miArray && miArray.length > 0) {

        const valorV = miArray.find(d => d && d.isSelect);

        let todosIguales = false;
        if (valorV && valorV?.motivation) {
          const valorComparacion = valorV?.motivation?.name;

          if (valorComparacion != undefined) {
            todosIguales = false;
          }


        } else if (miArray && miArray.length > 0 && miArray.find(own => own && own?.motivation)?.motivation) {
          const withMotivation: Motivation = miArray.find(own => own && own.motivation).motivation;
          const valorComparacion = withMotivation.name;

          for (const objeto of miArray) {
            if (objeto.motivation && objeto.motivation.name !== valorComparacion) {
              // Si encuentras un valor diferente, establece la variable "todosIguales" en false y rompe el bucle
              todosIguales = true;
              break;
            }
          }

        }

        if (miArray.every(e => !e.motivation)) {
          todosIguales = true;
        }

        return todosIguales;
      }
    }

    return false;

  }

  get mediumScreenAndDown(): boolean {
    return window !== undefined ? window.matchMedia(`(max-width: ${TableComponent.largeViewportWidth}px)`).matches : false;
  }

  @Input()
  set displayedColumns(displayedColumns: string[] | undefined) {
    this.$displayedColumns = displayedColumns;
  }
  get displayedColumns(): string[] | undefined {
    return this.$displayedColumns || this.config?.columnsFields?.map(value => value.id);
  }

  set selectAll(val: boolean | undefined) {
    if (this.config?.selectionMode === SelectionMode.multiple || (this.config?.selectionMode === SelectionMode.multipleWithBaseRoute)) {
      let sum = 0;
      this.selection.all = val;
      let sumaVerdaderaIn = 0;


      if (this.strategySelect && this.strategySelect.value) {

        this.tableData.data.forEach((item: any) => {
          if (this.isEnabled('select_row', item)) {

            sum += item.fixedPrice;

            if ((this.strategySelect && (item.fixedPrice <= this.strategySelect.value) && (sum <= this.strategySelect.value)) ||
              item.fixedPrice != undefined && item.fixedPrice === 0) {

              this.selectionStatus[this.getRowId(item)] = { selected: this.selection.all, row: item, not: true };
              sumaVerdaderaIn += item.fixedPrice;

            } else {
              if (this.strategySelect && (sum > this.strategySelect.value)) {
                sum = sumaVerdaderaIn;
              }
            }

          }
        });

      } else {
        this.tableData.data.forEach(item => {
          if (this.isEnabled('select_row', item)) {

            this.selectionStatus[this.getRowId(item)] = { selected: this.selection.all, row: item, not: true };

          }
        });

      }

      const selections: any[] = [];
      Object.keys(this.selectionStatus).forEach(k => {
        if (this.selectionStatus[k]?.selected) {
          this.selectionStatus[k].row.isSelect = true;
          selections.push(this.selectionStatus[k]);
        } else {
          this.selectionStatus[k].row.isSelect = false;
          // selections.push(this.selectionStatus[k]);
        }
      });

      this.selection.rows = [...selections];

      if (val == true) {
        this.hostColumnEditService.updatedRow(selections);
      }
      // qua viene la logica

      this.rowSelect.emit({ ...this.selection });

    }
  }

  get selectAll(): boolean | undefined {
    return this.selection.all;
  }

  //public isEndedScroll: boolean = false;

  ngOnInit(): void {

    this.subs.add(
      this.store.pipe(select(selectAuth)).subscribe(auth => {
        this.roles = auth && auth.roles && auth.roles.length > 0 ? [...auth.roles] : [];
      })
    );


    // non mi serve piu
    /*this.defaultLayoutService.scrollRead.pipe( debounceTime(50) ).subscribe(point => {
      if (point && point != 0) {
        this.isEndedScroll = true;
      } else {
        this.isEndedScroll = false;

      }

      console.log(this.isEndedScroll);
    });*/


    this.subs.add(
      this.store.pipe(select(selectFeatureCode), debounceTime(50), distinctUntilChanged()).subscribe(featureCode => {
        this.featureCode = featureCode || undefined;

      })
    );
    this.init();

    if (this.config?.summaryRow?.data$) {
      this.subs.add(this.config.summaryRow.data$.subscribe(data => this.summaryData = data));
    }

    // See `TableGroupRow.changes$`'s own comment. A caller flips `row.__expanded` in place, which
    // repaints that row's own bindings and nothing else -- WHICH ROWS EXIST is this component's
    // derivation, and only this rebuilds it. `renderRows()` as well as the rebuild: the rebuild
    // hands the CDK a new array reference (an input change it re-renders on), and the explicit
    // call covers the case where change detection has not yet propagated that input.
    if (this.config?.groupRow?.changes$) {
      this.subs.add(this.config.groupRow.changes$.subscribe(() => {
        this.rebuildRenderedRows();
        this.matTable?.renderRows();
      }));
    }

    this.setAvailable = false;
    this.hostColumnEditService.hostSetAllQuantity(false); // arriva il false al pulsante header in memoria
    this.subs.add(this.dataSource.subscribe(ds => {

      if ((this.featureCode == 'TRANS' || this.featureCode == 'OUT' || this.featureCode == 'session_detail')   /** && code == transferType */) {
        this.hostColumnEditService._resetStateData.pipe(distinctUntilChanged()).subscribe(des => {

          if (des) {
            this.clearValuesInputs();
          }
        })
      }

      if (this.getRowTable) {
        let cont = 0;

        ds.data = ds.data.map((d: any) => Object.assign(d, { ...d, idRowTable: cont += 1 })) as [];

      }

      this.sortStatus = {};
      this.clearSelection();
      ds.tableStatus.sort = ds.tableStatus.sort || [];
      this.tableData = ds;
      this.rebuildRenderedRows();
      this.config?.groupRow?.rowsLoaded?.(ds.data as any[]);

      // fixme IMPORTANTE santiago questo solo in featureCode == 'transferType'
      this.whHost = (ds.data as []);
      this.pageIndex = parseInt(this.tableData.tableStatus.page.pageIndex.toString()) + 1;


      // Positions compacted: an echoed field with no column must not leave a gap (see the helper).
      this.sortStatus = this.sortStatusFromEcho(ds.tableStatus.sort);

    }));

    // THE PENDING-DELETE HIGHLIGHT FOLLOWS THE STORE FOR THIS COMPONENT'S LIFE.
    //
    // It used to be subscribed with `take(1)` from INSIDE the dataSource handler above, which made
    // `statusPer` changeable only when the list reloaded. Every delete action dispatches
    // `RowSelect false` when the user cancels, and that dispatch reached nothing: the one-shot
    // subscription was already spent, so a cancelled delete left its row painted red until the
    // next reload. It appeared to work only because a `RowSelect` write used to re-issue the
    // search as a side effect (see the `isRowSelectChange` note in DefaultListComponent) -- so
    // fixing that alone would have made the stuck red PERMANENT. The two are one repair.
    this.subs.add(
      this.store.pipe(select(selectCurrentRow), distinctUntilChanged(), debounceTime(environment.defaultDebounceTime))
        .subscribe(data => this.selectRowEvident(data))
    );
  }

  ngAfterViewInit() {

    this.subs.add(
      merge(
        this.sort,
        this.paginator?.page.pipe(debounceTime(environment.defaultDebounceTime), distinctUntilChanged())
      ).pipe(
        map(() => {
          // Paging or sorting replaces the whole visible set: bring the table back to the top so the
          // reader is not left halfway down unrelated rows. Same stream, so both triggers are covered.
          this.scrollToTop.toTop(this.host);
          const tableStatus: TableStatus = this.tableData.tableStatus;
          // FIXME K3 potremmo intercettare il cambio di dati e lasciare la selezione per le sole righe visibili
          this.selectionStatus = {};

          this.selection = { all: false, rows: [] };

          this.rowSelect.emit(this.selection);

          if (this.paginator && this.config) {
            this.config.pageSize = this.paginator.pageSize;
            tableStatus.page = {
              pageIndex: this.paginator.pageIndex,
              pageSize: this.paginator.pageSize,
              length: this.paginator.length
            };
          }

          tableStatus.sort = _.sortBy(Object.values(this.sortStatus), 'position').map((sortItem: SortItem) => {
            return { field: sortItem.field.sortExpression, direction: sortItem.direction };
          });


          return tableStatus;
        })
      ).subscribe(tableStatus => {
        this.status.emit(tableStatus);
      })
    );
  }

  ngOnDestroy() {
    this.clearDataTableInputs();
    this.subs.unsubscribe();
    this.footerResizeObserver?.disconnect();
  }

  isEnabled(name: string | undefined, data?: any): any {
    const nameOfEvent = this.config?.actions?.find(d => name?.toLowerCase() == d.event?.toLowerCase());
    const arrToValidity = this.selection.rows;

    if (data) {
      this.notPosible[data.id];
    }
    // if (this.featureCode && (this.featureCode == 'santiSession' || this.featureCode == 'session')) {

    if (this.definitionValid && this.featureCode == 'santiSession') {

      if (data && data.motivation && data.motivation.name != this.definitionValid && name == 'select_row') {
        return false;
      }
    } else if (this.featureCode && this.featureCode == 'session') {
      if (this.selection.rows.length > 0) {
        this.definitionValid = this.selection.rows[0]?.row?.motivation?.name;
      } else {
        this.definitionValid = undefined;
      }

    }

    if (this.definitionValid && data && data.motivation && (data.motivation.name != this.definitionValid) && name == 'select_row') {
      return false;
    }



    if (this.strategySelect && (name == 'select_row' || name == 'wrapSession')) {
      if (data && data.fixedPrice != undefined) {
        if (this.strategySelect) {
          const a = [...arrToValidity?.map(d => d?.row)];
          // let sumaVerdadera = 0;
          let sumaDeSelects = 0;

          if (a && a.length > 0) {
            a.forEach(item => {
              if (item) {
                // sumaVerdadera = data.id == item.id ? item.fixedPrice : data.fixedPrice + item.fixedPrice;
                if (item.isSelect) {
                  sumaDeSelects += item.fixedPrice;
                }
                if (data && !data.isSelect && (sumaDeSelects + data.fixedPrice) > this.strategySelect?.value) {
                  this.notPosible[data.id] = false;
                } else {
                  this.notPosible[data.id] = true;
                }
              }
            });
          } else {
            this.notPosible[data.id] = true;
          }

          if ((data && data.id && data.fixedPrice > this.strategySelect.value)) {
            return false;
          }

          if (this.notPosible[data.id] == false) {
            return false;
          }

          return true;
        }

      }
    }

    if (nameOfEvent && nameOfEvent.acceptRole && nameOfEvent.acceptRole.length > 0) {
      return nameOfEvent.acceptRole.find(roleAcc => this.roles.find(role => role?.toLowerCase() == roleAcc?.toLowerCase()));
    }

    if ((this.config && this.config.isRecordEnabled && isFunction(this.config.isRecordEnabled))) {
      return this.config.isRecordEnabled(name, data);
    }

    if ((this.config?.actions?.find(d => name == d.event && d?.acceptRole?.find(rl => this.roles.find(onRole => rl == onRole))))) {
      return true;
    }

    return true;
  }

  clearSelection() {
    this.selectionStatus = {};
    this.selection = { rows: [], all: false };
  }


  get pageIndex() {
    return this.valuePagueIndex
  }

  set pageIndex(v: any) {
    this.valuePagueIndex = v;
  }

  goToPage(ev: KeyboardEvent | Event) {

    const quantitaPage = this.tableData.tableStatus.page.length / this.tableData.tableStatus.page.pageSize;
    let value = parseFloat((ev.target as any)?.value);
    const max = Math.ceil(quantitaPage);

    if ((value - 1) >= max) {
      value = max;
      this.dialogService.openSnack(this.translateService.instant('common.message.noMorePages', { max }), '', 5000);
    }

    if ((quantitaPage + 1) >= value || _.isNaN(value)) {
      this.paginator.pageIndex = isNaN(value) ? 0 : (value - 1);

      this.paginator.page.next({
        pageIndex: (this.paginator.pageIndex),
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });

    } else {
      this.dialogService.openSnack(this.translateService.instant('common.message.noMorePages', { max }), '', 5000);
    }
  }

  /**
   * The key this table files a row's selection state under.
   *
   * `JSON.stringify` is the last resort and has to stay one: a GROUP row has no id by design (the
   * grouped endpoint nulls every per-order field on a multi-order group) and carries its own
   * `orders` array, so stringifying it serialises the whole group's payload -- and `isSelected`
   * runs from the row template's `[ngClass]`, i.e. once per rendered row per change-detection
   * pass, on a page that re-enters `tick()` every few tens of milliseconds with no interaction at
   * all. The group token is a short, stable string (see `groupRow.keyOf`) that is already stamped
   * on exactly the rows with no id of their own, so it is read first.
   *
   * A CHILD row carries its parent's token too, but it is a real record with a real id, so it
   * never reaches the token branch and two rows can never share a key.
   */
  private getRowId(row: any) {
    if (row == null) {
      return JSON.stringify(row);
    }
    if (row.id) {
      return row.id;
    }
    return row.__groupToken != null ? row.__groupToken : JSON.stringify(row);
  }

  getActions(type: string): TableAction[] {

    return this.actions[type];
  }

  private actionTypeSelect(value: any) {
    this.hostColumnEditService.updatedDataSelectionALL(value);

  }

  private actionTypeInput(value: any) {
    this.hostColumnEditService.updatedDataSelectionALLInput(value);
  }

  setQuantity(e?: Event) {
    if (e) {
      e.preventDefault();
      this.setAvailable = !this.setAvailable;
    }

    if (this.setAvailable) {
      this.bindingType('insertQuantity').typeIdInput = '0';
      this.hostColumnEditService.hostSetAllQuantity(true);
    } else {
      this.hostColumnEditService.hostSetAllQuantity(false);
    }
  }

  bindingType(id: any) {
    const _me = this;
    return {
      get typeId(): any {
        return _me.typeObjectId[id]?.id ? _me.typeObjectId[id]?.id : _me.typeObjectId[id];
      },

      set typeId(typeOn: any | object) {
        if (typeOn == '' || typeOn == null) {
          typeOn = null;
        }

        if (isObject(typeOn)) { // cuando retorne un problema . obtengo el id y mando solo el numero al subject y hago el calculo en base al numero y no en base al objeto. :)
          _me.typeObjectId[id] = typeOn;
        } else if (!Number.isNaN(typeOn)) {
          _me.typeObjectId[id] = typeOn;
        }

        _me.actionTypeSelect(_me.typeObjectId);
      },

      get typeIdInput() {
        return _me.typeObjectId[id];
      },
      set typeIdInput(valueInput) {
        if (valueInput == '' || valueInput == null) {
          valueInput = null;
        }
        _me.typeObjectId[id] = valueInput;
        _me.actionTypeInput(_me.typeObjectId);

      },
    }
  }


  private init() {
    this.actions = {};

    if (this.config && !this.config.columnsFields && !this.displayedColumns) {
      this.displayedColumns = ['no_col_defined'];
    }
    if (this.config && !this.config.columnsFields && this.displayedColumns) {
      this.config.columnsFields = this.displayedColumns.map(value => new TableField(value, undefined, 'common.' + value));
    } /*else if (!this.displayedColumns && this.config.columnsFields) {
      this.displayedColumns = this.config.columnsFields.map( value => value.id);
    }*/

    this.loadConfig();

  }

  private loadConfig() {
    if (this.config) {
      this.selectMode = this.config.selectionMode || this.selectMode;
      if (!this.config.pageSize) {
        this.config.pageSize = 20;
      }

      // facciamo vedere sempre il sort clear
      if (this.config.actions || true) {
        const _SELF = this;
        if (this.config.actions) {
          Object.keys(TableActionType).forEach(value => {
            if (_SELF.config?.actions) {
              _SELF.actions[value] = _SELF.config.actions.filter(ac => ac?.type === value)
            }
          });
        }


        if (this.displayedColumns && this.getActions('RECORD').length > 0 && !this.displayedColumns.includes('actions')) {
          this.displayedColumns.push('actions');
        }



      }

      if (this.config.selectionMode === SelectionMode.multiple || (this.config.selectionMode === SelectionMode.multipleWithBaseRoute)) {

        if (this.selectionDeniedForRole()) {
          // Selection restricted to roles the current user lacks: behave as a
          // non-selectable table (no checkbox column, no select-all).
          this.selectMode = SelectionMode.none;
        } else if (this.displayedColumns && !this.displayedColumns.includes('multi_selection')) {
          this.displayedColumns.splice(0, 0, 'multi_selection');
        }

      }
    }
    // The selection mode decides which rows are navigable, so a config landing after the rows
    // would otherwise leave the tab stop on a row that no longer is one -- or on none at all.
    this.resolveTabStop();
  }

  /**
   * True when the config restricts selection to a role set (`selectionAcceptRole`) and the
   * current user holds none of them. Case-insensitive, matching the action `acceptRole`
   * comparison in isEnabled().
   */
  private selectionDeniedForRole(): boolean {
    const allowed = this.config?.selectionAcceptRole;
    if (!allowed || allowed.length === 0) {
      return false;
    }
    return !allowed.some(roleAcc =>
      this.roles.some(role => role?.toLowerCase() === roleAcc?.toLowerCase())
    );
  }

  private clearValuesInputs() {
    // fixme santiago controlla il dato
    this.config?.columnsFields?.forEach(column => {
      this.typeObjectId = {};
      this.setAvailable = false;
      this.setQuantity();
      this.bindingType(column.id).typeIdInput = null;
      this.bindingType(column.id).typeId = null;
    });
  }

  /**
   * How many sort keys the user can actually CLEAR -- which is what the clear-sort button's badge
   * counts and what its visibility is gated on.
   *
   * It reads `sortStatus`, not `tableData.tableStatus.sort`, and the difference is the whole
   * point. `tableStatus.sort` is the sort the SERVER echoed back in `data.meta.sort`, which can
   * legitimately contain a field this table renders no column for: `sortStatus` is rebuilt from it
   * by matching `columnsFields` on `sortExpression`, so an unmatched field is dropped there and is
   * the one thing {@link clearSort} cannot remove.
   *
   * Gating on the echoed list showed a permanent badge on any list whose route sets
   * `enforceDefaultSort` -- the grouped dashboard appends `groupKey` as a tiebreak to every search,
   * `groupKey` has no column, so the control was visible with a count of at least 1 before the user
   * had sorted anything, and pressing it was a no-op in effect: `clearSort()` emits `sort: []`, the
   * next search re-appends the tiebreak, and the badge came straight back.
   */
  get clearableSortCount(): number {
    return Object.keys(this.sortStatus || {}).length;
  }

  clearSort() {
    this.sortStatus = {};
    this.sortSubject.next(this.sortStatus);
  }
  selectRowEvident(data: RowSelect) {
    this.statusPer = data;
  }

  /**
   * True when `row` is covered by the delete a `RowSelect` write is currently confirming -- the
   * red pre-delete highlight. It answers for a GROUP as well as a row, which is why it is a method
   * rather than the expression it replaces (`row.id == statusPer.row.id`, inline in the template).
   *
   * That expression had two defects beyond being unable to see a group. It compared with `==`, so
   * `null == undefined` held and every id-LESS row matched every other one -- the grouped
   * dashboard's aggregates carry a null id by design, so marking one reddened them all. And it
   * could only ever match by id, which an aggregate does not have, so a group could not be marked
   * at all.
   *
   * TWO tests, and NEITHER may be object identity. The mark reaches here through the store, and
   * `selectCurrentDynaData` deep-CLONES the slot it returns, so `statusPer.row` is never the same
   * object this table holds -- an identity test is dead code that reads as if it works. So: the
   * group TOKEN, stamped on a group and every child spliced under it by `rebuildRenderedRows`,
   * which is what reddens all the orders a group delete would take; then a strict id match, which
   * is what an ordinary row has and what survives a reload replacing the row objects.
   *
   * `!marked.__groupChild` is what keeps the two apart, and it is not a detail: a CHILD carries its
   * group's token too, so without it, deleting one order out of a group reddened the whole group --
   * telling the operator that confirming would take all twenty-five when it takes one. Marking a
   * child falls through to the id match, which is exactly the single row it names.
   *
   * KNOWN LIMITATION, WIDENED BY MAKING THAT SUBSCRIPTION LIVE: the mark is read through
   * `selectCurrentRow`, which is AMBIENT (`dynaData[the active route]`), so every `TableComponent`
   * on screen reads the SAME slot -- and the id match below carries no notion of which table the
   * mark came from. Two markable tables under one route would therefore redden each other's rows
   * whenever two of their ids collide. There is no live instance: the two screens that do render a
   * second table under one route (`detail-session-wrapper`'s `onlycheck` picker and the nested list
   * in `all-sessions-into-wrap`) host no action that dispatches `RowSelect` at all, so no mark is
   * ever produced there. It was unreachable before only by accident -- the subscription was
   * `take(1)` and spent at load -- so it is recorded rather than fixed: scoping it needs a table
   * identity plumbed through `ActionData`/`ColumnData` to all four dispatchers, or a stamp on every
   * row of every table in the app, and a stamp would ride into the save payloads that send
   * `this.data.item` straight to the backend. IF YOU ADD A SECOND ROW-MARKING TABLE TO ONE ROUTE,
   * that plumbing is the work, and it is not optional.
   *
   * THE FOUR FIELDS READ HERE ARE THE WHOLE CONTRACT WITH A DISPATCHER, and one of them relies on
   * it: `GroupedDeleteRowComponent` marks a TRIMMED object carrying exactly `id`, `__groupToken`
   * and `__groupChild`, because the aggregate row it would otherwise have sent carries the group's
   * full `Order` objects and the mark is persisted (see that component's own comment). So a field
   * added to this test has to be added to that marker too, or the highlight silently stops covering
   * the rows the press would delete.
   *
   * `select` IS READ, and it was not. `RowSelect` declares it and every dispatcher set it, so a
   * mark carrying `select: false` used to paint the row anyway -- a field of the shared model that
   * said the opposite of the screen, and dead state is how a wrong answer gets shipped: the five
   * dispatchers all wrote it from a `this.toRow = !this.toRow` toggle that nothing reset, so the
   * second press on one row sent `false` and was silently ignored. They mark unconditionally now,
   * and honouring the flag is what keeps the two halves from drifting apart again. It is read
   * `!== false` rather than for truthiness: a marker that omits the field entirely is still a
   * mark, which is the reading every caller has always had.
   */
  isPendingDelete(row: any): boolean {
    const mark = this.statusPer as any;
    const marked = mark?.row;
    if (!marked || !row || mark.select === false) {
      return false;
    }
    if (marked.__groupToken != null && !marked.__groupChild && row.__groupToken === marked.__groupToken) {
      return true;
    }
    return row.id != null && marked.id != null && row.id === marked.id;
  }

  // TODO: qua dovrei lavorare per aprire il modale

  /**
   * Resolves the navigation target for a cell: column link first, then table rowLink, else null.
   *
   * A column that DEFINES a `link` owns its own cell outright -- including the decision NOT to
   * link. `link: () => null` is an OPT-OUT, and the whole expression used to be one `??` chain,
   * which cannot tell "this column declined" from "this column has no link": the opt-out fell
   * through to `rowLink` and the cell was wrapped in an anchor anyway. On a tablet that turns a tap
   * meant to reveal a tooltip into a navigation, which is exactly what the kiosk fleet's signals
   * column exists to avoid. So the PRESENCE of the callback decides who answers, and its return
   * value is the answer.
   */
  resolveLink(column: TableField, row: any): any[] | null {
    if (column?.link) {
      return column.link(row) ?? null;
    }
    return this.config?.rowLink?.(row) ?? null;
  }

  /** True when a sum row is configured and there are totals to render. */
  get showSummaryRow(): boolean {
    return !!this.config?.summaryRow && !!this.summaryData;
  }

  /** Formatted sum-row cell text for a column id (empty string when the column has no total). */
  summaryCell(columnId: string): string {
    if (!this.summaryData) { return ''; }
    return this.config?.summaryRow?.cells?.[columnId]?.(this.summaryData) ?? '';
  }

  /**
   * True when `config.groupRow` claims this row is an expandable group. A table with no
   * `groupRow` answers false for every row, which is what keeps this opt-in.
   */
  isGroupRow(row: any): boolean {
    return this.config?.groupRow?.isGroup?.(row) ?? false;
  }

  /**
   * What the table actually RENDERS: the server's rows, with each expanded group's children
   * spliced in directly beneath their parent.
   *
   * Deliberately a SECOND array rather than a mutation of `tableData.data`. Everything that
   * describes the RESULT SET keeps reading the raw one -- the paginator's length, select-all,
   * and `DefaultListComponent`'s sort sanitiser -- because children are a display concern: the
   * server never paged them, `totalElements` counts groups, and a select-all that silently swept
   * up rows the user expanded would act on a set they never chose.
   *
   * A table with no `groupRow` gets `tableData.data` back untouched, which is what keeps the
   * whole capability opt-in.
   */
  renderedRows: any[] = [];

  /**
   * The id of the first column that renders a VALUE (not the selection checkbox, not the actions).
   * A highlighted child's screen-reader label is rendered there, once per row: in every value cell
   * it would be read once per column.
   */
  get firstValueColumnId(): string | undefined {
    return this.displayedColumns?.find(id => id !== 'multi_selection' && id !== 'actions');
  }

  /**
   * Rebuilt on a new data emission and whenever `groupRow.changes$` fires. Always assigns a NEW
   * array: `[dataSource]` is bound to this, and the CDK re-renders on an input change but cannot
   * see a mutation in place.
   */
  private rebuildRenderedRows(): void {
    const hadRowFocus = this.rowHasFocus();
    this.buildRenderedRows();
    this.resolveTabStop();
    if (hadRowFocus) {
      this.restoreRowFocus();
    }
  }

  private buildRenderedRows(): void {
    const rows = this.tableData?.data ?? [];
    const groupRow = this.config?.groupRow;
    if (!groupRow) {
      this.renderedRows = rows.slice();
      return;
    }
    const rendered: any[] = [];
    // The zebra BAND is counted over top-level rows only, and a child carries its parent's.
    // `:nth-child(odd)` counts rendered rows, so once children are spliced in the stripe walks
    // through a group and restarts the parents on the wrong parity below it -- the band stops
    // meaning "the next record" and starts meaning "the next line". Counting it here is the only
    // place that knows which rows are records; the CSS reads the stamp (see `row-band-alt`).
    //
    // OFFSET BY ONE, so the FIRST record is the tinted one. `:nth-child(odd)` tints the first ROW
    // on every other list in the app, and the flat and grouped dashboards are one screen in two
    // arrangements of the same orders -- with a bare `band % 2` the tint started on the SECOND
    // record, so the stripe visibly shifted by a row every time the user pressed the toggle.
    // `band` itself still counts from 0, because `groupTokenOf`'s positional fallback below is
    // documented as "its index among the SERVER's rows" and has to keep being exactly that.
    let band = 0;
    for (const row of rows as any[]) {
      row.__band = (band + 1) % 2;
      const isGroup = groupRow.isGroup(row);
      // Only a real GROUP is tokened. Without that guard two ordinary rows would share a token and
      // marking one would highlight the other.
      //
      // DERIVED FROM THE ROW WHEN THE CALLER CAN SAY SO (`groupRow.keyOf`), and positional only as
      // a fallback. The token outlives this render: `isPendingDelete` matches it against a row
      // that travelled through the store, and that slot is PERSISTED -- so a positional token read
      // back after a reload, a re-sort or a page change names whichever group now sits at that
      // index, reddening another customer's orders with no delete pending. See `keyOf`'s own
      // comment. The `g`/`k` prefixes keep the two kinds from ever colliding.
      row.__groupToken = isGroup ? this.groupTokenOf(row, band) : undefined;
      band++;
      // The block flags the border hangs off. Assigned on EVERY pass, never only when true: rows
      // outlive a rebuild, so a group that collapses would otherwise keep drawing the top edge of
      // a block that is no longer open.
      const open = isGroup && groupRow.isExpanded(row);
      row.__groupBlockStart = open;
      row.__groupBlockEnd = false;
      rendered.push(row);
      if (!open) {
        continue;
      }
      const children = (groupRow.childrenOf(row) ?? []) as any[];
      // An expanded group with nothing under it closes its own block, or the border is left open.
      if (!children.length) {
        row.__groupBlockEnd = true;
      }
      for (const child of children) {
        // Stamped so the template can tell a child from a top-level row without re-deriving the
        // parent relationship -- that is what the indent hangs off, and a child must never be
        // mistaken for a group (it has no children of its own to expand).
        child.__groupChild = true;
        // A TOKEN, never the parent object. Two separate reasons, and the first one crashed:
        //
        // `getRowId` falls back to `JSON.stringify(row)` for a row with no id -- which is exactly
        // what an aggregate is (the backend nulls every per-order field on a multi-order group) --
        // and a child holding its parent while the parent holds `orders: [child]` is a CYCLE.
        // `isSelected` runs from the row template, so every change-detection pass over an expanded
        // group threw `Converting circular structure to JSON`.
        //
        // And identity could not have worked anyway: the mark travels through the store, and
        // `selectCurrentDynaData` deep-CLONES its slot, so the row `isPendingDelete` compares
        // against is never the same object the table holds. A token survives a clone; a reference
        // does not. The token is the group's own stable key where the conf declares one
        // (`groupRow.keyOf`), and otherwise its index among the SERVER's rows -- neither moves when
        // a group expands (children are not in `tableData.data`), so a mark taken before an
        // expansion still matches after it. Only the keyed form also survives a reload, a re-sort
        // or a page change, which is why the positional one is a fallback rather than the design.
        child.__groupToken = row.__groupToken;
        child.__band = row.__band;
        // Re-evaluated per pass rather than set once: the membership can change under a reload, and
        // a child that stops being last must stop drawing the bottom edge.
        child.__groupBlockEnd = child === children[children.length - 1];
        // ALWAYS assigned, never only when true: a row object outlives a rebuild, and a highlight
        // from the previous search must not stay painted once the predicate stops naming it.
        child.__highlight = !!groupRow.isHighlighted?.(child, row);
        rendered.push(child);
      }
    }
    this.renderedRows = rendered;
  }

  /**
   * A group's token: the caller's own stable key where it declares one, else the row's index among
   * the server's rows. Distinctly prefixed so a conf that starts declaring `keyOf` cannot produce a
   * value that matches a positional token marked before it did.
   */
  private groupTokenOf(row: any, band: number): string {
    const key = this.config?.groupRow?.keyOf?.(row);
    return key == null || key === '' ? `g${band}` : `k${key}`;
  }

  /** True when this row was spliced in as a group's child, rather than returned by the server. */
  isGroupChild(row: any): boolean {
    return !!row?.__groupChild;
  }

  // ------------------------------------------------------------------ keyboard row navigation
  //
  // A row whose CLICK does something -- a group row's expand, a single-select pick, the navigation
  // a selection drives through `DefaultListComponent` -- used to be reachable by pointer alone
  // (WCAG 2.1.1). The rows are a roving tabindex: the table is ONE tab stop, exactly one navigable
  // row carries tabindex 0 and the rest -1, the arrow keys (and Home/End) move between them, and
  // Enter or Space does what a click does. A tab stop per row would put a hundred stops between the
  // filter and the paginator -- the bypass problem the status-throughput chart's roving tabindex
  // exists to avoid.
  //
  // Enter/Space DISPATCH A CLICK on the row, with the key's modifiers, rather than calling
  // `onRowClick`: half of what a click means happens OUTSIDE this component. `DefaultListComponent`
  // opens a ctrl/meta click in a new tab from a host `click` listener, reading the selection the
  // row click has just emitted, so only a real click event reaching it gives the keyboard the same
  // two behaviours a pointer has. A row in a `rowLink` list is not navigable: its cells are anchors,
  // which the keyboard already reaches, and the row click does nothing an anchor does not.

  /** The row holding tabindex 0. Kept by object, re-found by key when the rows are rebuilt. */
  private tabStopRow: any = null;

  /**
   * The rendered rows `rowKeyboardOutcome` answers non-null for, in `renderedRows` order. Computed once per
   * rebuild or config change (`resolveTabStop`), never per binding: `rowTabindex` runs for every row
   * on every change-detection pass, and asking `rowLink` there allocated a command array per row per
   * pass on every core list.
   */
  private navigableRows: any[] = [];
  private navigableSet = new Set<any>();

  /**
   * The table's accessible name while any row is navigable: the arrow-key hint, worded for what
   * Enter does on THESE rows. One sentence for every table told a grouped dashboard "Enter to open
   * one" over rows that expand, and a picker the same over rows that pick. Null with nothing
   * navigable, so the table carries no name at all.
   */
  rowNavigationHintKey: string | null = null;

  /**
   * Whether a selected row OPENS something in the host (a page, a tab). The table cannot know: a
   * single-select click only emits `rowSelect`, and whether that leads anywhere is the host's
   * business -- `DefaultListComponent` navigates only when its conf has a `baseRoute`. So the host
   * says so. Left undefined, a host that listens to `rowSelect` is taken to PICK on it (a dialog's
   * row picker) and one that does not listen gets no row tab stop, since its click does nothing.
   */
  @Input() rowOpens?: boolean;

  /** A row has focus that a rebuild took away; put back once the new rows have rendered. */
  private pendingRowFocusRestore = false;

  /** A group row the config can toggle -- the one test `onRowClick` and the keyboard share. */
  private isToggleGroup(row: any): boolean {
    const groupRow = this.config?.groupRow;
    return !!groupRow?.toggle && !!groupRow.isGroup(row);
  }

  /**
   * What a click on this row does, by `onRowClick`'s own branches -- the ONE statement of them, so
   * the keyboard model cannot drift from the pointer's. Note the precedence: `onlyCheck` turns a
   * `multipleWithBaseRoute` click into a new-page one, but a `single` click stays a selection.
   */
  private rowClickAction(row: any, onlyCheck = this.onlycheck): 'toggle' | 'select' | 'newPage' | null {
    if (this.isToggleGroup(row)) {
      return 'toggle';
    }
    if (this.selectMode === SelectionMode.single || (this.selectMode === SelectionMode.multipleWithBaseRoute && !onlyCheck)) {
      return 'select';
    }
    return onlyCheck ? 'newPage' : null;
  }

  /**
   * How a keyboard activation of this row would land: `'expand'` for a group, `'open'` when it
   * opens something, `'pick'` when the host only takes the selection, null when it does nothing a
   * user could notice -- in which case the row gets no tab stop and is not counted in the hint.
   *
   * "Does something" is measured, not assumed from the select mode: the default mode is `single`,
   * so reading it alone made every row of every list navigable and announced "Enter to open one"
   * on lists whose click selects nothing anyone reads. A row the config refuses to select is not
   * navigable either -- its click clears the selection and nothing else.
   */
  private rowKeyboardOutcome(row: any): 'expand' | 'open' | 'pick' | null {
    const action = this.rowClickAction(row);
    if (action === 'toggle') {
      return 'expand';
    }
    if (!action || this.config?.rowLink?.(row) != null || !this.isEnabled('select', row)) {
      return null;
    }
    if (action === 'newPage') {
      return this.newPageSingle.observed ? 'open' : null;
    }
    if (this.config?.rowDialog || this.rowOpens) {
      return 'open';
    }
    return this.rowOpens === undefined && this.rowSelect.observed ? 'pick' : null;
  }

  /** The row's tabindex: null (no attribute) when the row does nothing, else 0 for the tab stop. */
  rowTabindex(row: any): number | null {
    if (!this.navigableSet.has(row)) {
      return null;
    }
    return row === this.tabStopRow ? 0 : -1;
  }

  /**
   * A group row's open/closed state, as the key of the visually-hidden text rendered in its first
   * value cell; null on every other row. Not `aria-expanded` on the row: the CDK row is
   * `role="row"` inside a `role="table"`, and ARIA honours `aria-expanded` on a row only in a
   * treegrid, so assistive tech may drop it. There is no `aria-selected` either, deliberately: a
   * single-select click never leaves `isSelected` true (`onSelectOnlySingle` files the clicked row
   * as `selected: false` -- the selection is the emission, and the list usually navigates on it), so
   * the attribute would announce "not selected" on every row, always.
   */
  groupStateKey(row: any): string | null {
    if (!this.isToggleGroup(row)) {
      return null;
    }
    return this.config!.groupRow!.isExpanded(row) ? GROUP_STATE_KEYS.expanded : GROUP_STATE_KEYS.collapsed;
  }

  /** A pointer (or anything else) focusing a row moves the tab stop with it. */
  onRowFocus(row: any): void {
    if (this.navigableSet.has(row)) {
      this.tabStopRow = row;
    }
  }

  onRowKeydown(row: any, event: KeyboardEvent): void {
    const el = event.currentTarget as HTMLElement | null;
    // A key from a control INSIDE the row (a checkbox, an anchor, an action button) is that
    // control's own: answering it here would doubly activate it, or steal its arrow keys.
    if (!el || event.target !== el || !this.navigableSet.has(row)) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      // Space would scroll the page. An auto-repeating Enter would toggle a group open and shut,
      // or fire a navigation per repeat; a held key is one press.
      event.preventDefault();
      if (event.repeat) {
        return;
      }
      el.dispatchEvent(new MouseEvent('click', {
        bubbles: true, cancelable: true,
        ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey, altKey: event.altKey,
      }));
      return;
    }
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    const edge = event.key === 'Home' ? 'first' : event.key === 'End' ? 'last' : null;
    if (!step && !edge) {
      return;
    }
    const navigable = this.navigableRows;
    const at = navigable.indexOf(row);
    const to = edge === 'first' ? 0 : edge === 'last' ? navigable.length - 1 : at + step;
    const target = to >= 0 && to < navigable.length ? this.rowElementOf(navigable[to]) : undefined;
    // Prevented only once a move is certain: at the first or last row the key is the page's again,
    // or ArrowDown on the last row could never scroll down to the paginator.
    if (at < 0 || to === at || !target) {
      return;
    }
    event.preventDefault();
    this.moveTabStop(navigable[to], el, target);
  }

  private moveTabStop(row: any, from: HTMLElement | undefined, to: HTMLElement): void {
    this.tabStopRow = row;
    // Written straight to the DOM as well as through the binding, so focus can move NOW rather
    // than after the next change-detection pass; the binding lands on the same values.
    from?.setAttribute('tabindex', '-1');
    to.setAttribute('tabindex', '0');
    // `preventScroll`, then `revealRow`: the browser's own focus scroll ignores the sticky header
    // and sum rows, so a row reached at the edge of the view landed underneath one of them, focus
    // ring and all (WCAG 2.4.11).
    to.focus({ preventScroll: true });
    this.revealRow(to);
  }

  /**
   * Scroll a row into view clear of THIS table's sticky header and footer rows. `scrollIntoView`
   * brings it to the nearest edge of its scroll container; a sticky row stuck to that edge then
   * covers it, so the overlap is measured and scrolled away. A header or footer that is not stuck
   * sits before or after every body row in flow and can never overlap one, so no special case is
   * needed for it; a hidden sum row measures zero and is skipped. The paginator bar (`.cust_`) is
   * `position: fixed` over the bottom of the viewport, so a row brought to the bottom edge lands
   * under it as well: its top bounds the view from below too (found the way `syncFooterToContent`
   * finds it; absent with `paginatorMode: 'none'`).
   */
  private revealRow(row: HTMLElement): void {
    row.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const table = this.tableEl?.nativeElement;
    if (!table) {
      return;
    }
    const header = table.querySelector<HTMLElement>(':scope > mat-header-row');
    const footer = table.querySelector<HTMLElement>(':scope > mat-footer-row');
    const rect = row.getBoundingClientRect();
    const top = header && header.offsetHeight > 0 ? header.getBoundingClientRect().bottom : -Infinity;
    const bar = this.tableContainerEl?.querySelector<HTMLElement>('.cust_');
    const bottom = Math.min(
      footer && footer.offsetHeight > 0 ? footer.getBoundingClientRect().top : Infinity,
      bar && bar.offsetHeight > 0 ? bar.getBoundingClientRect().top : Infinity,
    );
    const delta = rect.top < top ? rect.top - top : rect.bottom > bottom ? rect.bottom - bottom : 0;
    if (delta) {
      scrollContainerOf(row).scrollBy(0, delta);
    }
  }

  /**
   * A rendered row's element, found by its position in `renderedRows` -- which is DOM order -- among
   * THIS table's rows only, the `<mat-table>`'s direct children (a subtree query would also match a
   * row of a table a cell renders). Matched by position rather than by the `tabindex` the binding
   * writes, because that attribute lags the model until the next change-detection pass: an
   * `onlycheck` flip changes which rows are navigable at once, and a lookup keyed on the attribute
   * swallowed every arrow press until something re-rendered. Undefined while the DOM is mid-render
   * and holds a different number of rows than the model.
   */
  private rowElementOf(row: any): HTMLElement | undefined {
    const table = this.tableEl?.nativeElement;
    const rendered = this.renderedRows ?? [];
    if (!table) {
      return undefined;
    }
    const els = Array.from(table.querySelectorAll<HTMLElement>(':scope > mat-row'));
    return els.length === rendered.length ? els[rendered.indexOf(row)] : undefined;
  }

  /**
   * Keep the tab stop on the same RECORD across a rebuild, and fall back to the first navigable row
   * only when it is gone. A reload replaces the row objects, so it is re-found by key; re-pointing it
   * unconditionally would leave focus on one row and the tab stop on another.
   */
  private resolveTabStop(): void {
    const outcomes = new Set<string>();
    const navigable = (this.renderedRows ?? []).filter((r) => {
      const outcome = this.rowKeyboardOutcome(r);
      if (outcome) {
        outcomes.add(outcome);
      }
      return outcome !== null;
    });
    this.navigableRows = navigable;
    this.navigableSet = new Set(navigable);
    this.rowNavigationHintKey = rowNavigationHintFor(outcomes);
    if (this.tabStopRow && navigable.includes(this.tabStopRow)) {
      return;
    }
    const key = this.tabStopRow ? this.getRowId(this.tabStopRow) : undefined;
    this.tabStopRow = (key !== undefined && navigable.find((r) => this.getRowId(r) === key)) || navigable[0] || null;
  }

  private rowHasFocus(): boolean {
    const active = document.activeElement;
    return !!active && active.tagName === 'MAT-ROW' && !!this.tableEl && active.parentElement === this.tableEl.nativeElement;
  }

  /**
   * A rebuild that replaced the focused row's node took the focus with it, to <body>. It is put back
   * on the tab stop in `ngAfterViewChecked` -- the same task that rendered the new rows, since the
   * CDK table renders them while this view is checked -- and only if nothing else has taken focus.
   * It was a `setTimeout`, which left focus on <body> for a task (long enough for a screen reader
   * to announce the document) and could pull focus back from wherever a click had put it meanwhile.
   */
  private restoreRowFocus(): void {
    this.pendingRowFocusRestore = true;
  }

  ngAfterViewChecked(): void {
    if (!this.pendingRowFocusRestore) {
      return;
    }
    this.pendingRowFocusRestore = false;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) {
      return;
    }
    // `preventScroll`: the user may have scrolled away from the row with the wheel while it kept
    // focus, and a background reload must not drag the viewport back to it.
    // With no navigable row left (an empty page, every row refused) focus goes to the table itself
    // rather than staying on <body>, which a screen reader announces as the whole document. The
    // table takes `tabindex="-1"`: focusable from script, never a tab stop.
    const table = this.tableEl?.nativeElement;
    const stop = table?.querySelector<HTMLElement>(':scope > mat-row[tabindex="0"]');
    if (stop) {
      stop.focus({ preventScroll: true });
    } else if (table) {
      table.setAttribute('tabindex', '-1');
      table.focus({ preventScroll: true });
    }
  }

  onRowClick(row: any, ev: PointerEvent | MouseEvent, onlyCheck?: boolean) {
    // A GROUP ROW OWNS ITS OWN CLICK. It is not a record -- no id, no `rowLink`, and
    // `isRecordEnabled` refuses to select it -- so the selection path below has nothing to do with
    // it, and `groupRow.toggle` is what the row actually means.
    //
    // `stopPropagation` is half the point. The click used to travel on to
    // `DefaultListComponent`'s host listener, which opens a tab from whatever selection IT is
    // holding -- and the table never tells it that this row selected nothing, so that was some
    // earlier row, through a `baseRoute` a grouped conf need not even declare. Stopping the click
    // at the row it belongs to closes that without any of it reaching the store.
    //
    // MODIFIERS INCLUDED. What a click on a group means depends on them -- plain expands, ctrl/meta
    // means "open all of them" -- but that is the caller's decision, not this table's, so the event
    // goes over with the row. Handling only the unmodified case left ctrl/meta falling through to
    // the host listener, i.e. the stray tab this branch exists to stop.
    const action = this.rowClickAction(row, onlyCheck);
    if (action === 'toggle') {
      ev.stopPropagation();
      this.config!.groupRow!.toggle!(row, ev);
      return false;
    }

    if (action === 'select') {
      this.onSelectOnlySingle(row, ev.ctrlKey || ev.metaKey);
    } else if (action === 'newPage') {
      this.onSelectOnlySingleonNewPage(row, ev.ctrlKey || ev.metaKey);
    }




    return false;
  }


  selectionStatusNewPage: any;

  onSelectOnlySingleonNewPage(selectedRow: any, ctrlKey?: boolean) {
    const rowId = this.getRowId(selectedRow?.row || selectedRow);

    // aqui
    const rowSelectionStatus = (selectedRow?.row != null) || (selectedRow != null) && !this.isSelected(selectedRow.row || selectedRow);
    this.selectionStatusNewPage = {};
    this.selectionStatus[rowId] = { row: selectedRow?.row || selectedRow, selected: !rowSelectionStatus, ctrlKey: ctrlKey };

    if (this.isEnabled('select', selectedRow?.row || selectedRow)) {

      this.selection = selectedRow?.row || selectedRow ? { rows: [selectedRow?.row || selectedRow] } : { rows: [] };

      console.log(this.selection);

      this.selection.ctrlKey = ctrlKey;
      this.newPageSingle.emit({ ...this.selection });
    } else {
      this.selection = { rows: [] };
    }

  }

  isSelected(row: any) {
    const rowId = this.getRowId(row);
    return this.selectionStatus && this.selectionStatus[rowId] && this.selectionStatus[rowId]?.selected && !this.selectionStatus[rowId]?.ctrlKey || false;
  }

  onSelectOnlySingle(selectedRow: any, ctrlKey?: boolean) {
    const rowId = this.getRowId(selectedRow?.row || selectedRow);

    // aqui
    const rowSelectionStatus = (selectedRow?.row != null) || (selectedRow != null) && !this.isSelected(selectedRow.row || selectedRow);
    this.selectionStatus = {};
    this.selectionStatus[rowId] = { row: selectedRow?.row || selectedRow, selected: !rowSelectionStatus, ctrlKey: ctrlKey };

    if (this.isEnabled('select', selectedRow?.row || selectedRow)) {

      this.selection = selectedRow?.row || selectedRow ? { rows: [selectedRow?.row || selectedRow] } : { rows: [] };
      this.selection.ctrlKey = ctrlKey;
      this.rowSelect.emit({ ...this.selection });
    } else {
      this.selection = { rows: [] };
      // REVERTED, deliberately, and the reason is worth keeping. Emitting the cleared selection
      // here is the right SIGNAL -- a consumer holds its own copy, so clearing only this one leaves
      // it describing whatever row was selected before, which is how a ctrl+click on an aggregate
      // opened a tab for some earlier row. But the emission reaches the store
      // (`DefaultListComponent.select` dispatches SelectionChange), and a click on a group then
      // collapsed it: something on that path re-issues the search, and a reload replaces the row
      // objects `__expanded` is stamped on. A refused click must not be able to reload the list.
      //
      // So the stale-selection bug is still OPEN and must be closed somewhere that does not touch
      // the store. Do not re-add this emit without first establishing what the store emission
      // actually triggers -- that is the unknown, not this line.
    }


    if (this.config && this.config.rowDialog) {
      this.dialog.open(DialogRowComponent, {
        data: selectedRow?.row || selectedRow,
        height: '80%',
        width: '80%',
        disableClose: true
      });
    }
  }

  /** Re-resolves the row tab stop on a change, since it decides which rows are navigable. */
  @Input() set onlycheck(value: boolean | undefined) {
    this._onlycheck = value;
    this.resolveTabStop();
  }
  get onlycheck(): boolean | undefined {
    return this._onlycheck;
  }
  private _onlycheck: boolean | undefined;

  onSelectCheck(selectedRow: any, ctrlKey?: boolean) {

    console.log('only check');

    const rowId = this.getRowId(selectedRow?.row || selectedRow);

    if ((this.selectMode === SelectionMode.multiple) || (this.selectMode === SelectionMode.multipleWithBaseRoute)) {

      if (selectedRow.row && selectedRow.row.motivation && selectedRow.row.motivation.name) {
        if (!selectedRow.row.isSelect) {
          this.definitionValid = selectedRow.row.motivation.name;
        } else {
          if (this.selection?.rows?.length == 1) {
            this.definitionValid = undefined;
          }
        }
      }


      if (this.isEnabled('select', selectedRow?.row || selectedRow)) {


        const rowSelectionStatus = (this.selectionStatus[rowId]?.selected || false);
        this.selectionStatus[rowId] = this.selectionStatus[rowId] || selectedRow?.not ? { selected: false, row: selectedRow?.row || selectedRow, not: selectedRow.not, ctrlKey: ctrlKey } : { selected: false, row: selectedRow?.row || selectedRow, ctrlKey: ctrlKey };
        this.selectionStatus[rowId].selected = (selectedRow?.row != null || selectedRow != null) && !rowSelectionStatus;
        const selections: any[] = [];

        Object.keys(this.selectionStatus).forEach(k => {
          if (this.selectionStatus[k]?.selected) {
            this.selectionStatus[k].row.isSelect = true;
            selections.push(this.selectionStatus[k]);
          } else {
            this.selectionStatus[k].row.isSelect = false;
          }
        });

        this.selection = { rows: [...selections] };

        this.selection.all = this.tableData.data.filter(row => this.isEnabled('select', row)).every(row => this.selectionStatus[this.getRowId(row)]?.selected);

        this.selection.ctrlKey = ctrlKey;

        if (ctrlKey) {
          this.selection.rows = this.selection.rows.filter(d => d.row.id == selectedRow.id);
        }

        this.rowSelect.emit({ ...this.selection });
      }
    }

  }

  onSelect(selectedRow: any, ctrlKey?: boolean) {

    const rowId = this.getRowId(selectedRow?.row || selectedRow);

    if (this.selectMode === SelectionMode.single) {
      const rowSelectionStatus = (selectedRow?.row != null) || (selectedRow != null) && !this.isSelected(selectedRow.row || selectedRow);
      this.selectionStatus = {};
      this.selectionStatus[rowId] = { row: selectedRow?.row || selectedRow, selected: !rowSelectionStatus, ctrlKey: ctrlKey };

      if (this.isEnabled('select', (selectedRow?.row || selectedRow))) {
        this.selection = selectedRow?.row || selectedRow ? { rows: [selectedRow?.row || selectedRow] } : { rows: [] };
        this.selection.ctrlKey = ctrlKey;
        this.rowSelect.emit({ ...this.selection });
      } else {
        this.selection = { rows: [] };
      }

      if (this.config && this.config.rowDialog) {

        this.dialog.open(DialogRowComponent, {
          data: selectedRow?.row || selectedRow,
          height: '80%',
          width: '80%',
          disableClose: true
        });

      }

    } else if ((this.selectMode === SelectionMode.multiple) || (this.selectMode === SelectionMode.multipleWithBaseRoute)) {

      if (selectedRow.row && selectedRow.row.motivation && selectedRow.row.motivation.name) {
        if (!selectedRow.row.isSelect) {
          this.definitionValid = selectedRow.row.motivation.name;
        } else {
          if (this.selection?.rows?.length == 1) {
            this.definitionValid = undefined;
          }
        }
      }


      if (this.isEnabled('select', selectedRow?.row || selectedRow)) {


        const rowSelectionStatus = (this.selectionStatus[rowId]?.selected || false);
        this.selectionStatus[rowId] = this.selectionStatus[rowId] || selectedRow?.not ? { selected: false, row: selectedRow?.row || selectedRow, not: selectedRow.not, ctrlKey: ctrlKey } : { selected: false, row: selectedRow?.row || selectedRow, ctrlKey: ctrlKey };
        this.selectionStatus[rowId].selected = (selectedRow?.row != null || selectedRow != null) && !rowSelectionStatus;
        const selections: any[] = [];

        Object.keys(this.selectionStatus).forEach(k => {
          if (this.selectionStatus[k]?.selected) {
            this.selectionStatus[k].row.isSelect = true;
            selections.push(this.selectionStatus[k]);
          } else {
            this.selectionStatus[k].row.isSelect = false;
          }
        });

        this.selection = { rows: [...selections] };

        this.selection.all = this.tableData.data.filter(row => this.isEnabled('select', row)).every(row => this.selectionStatus[this.getRowId(row)]?.selected);

        this.selection.ctrlKey = ctrlKey;

        if (ctrlKey) {
          this.selection.rows = this.selection.rows.filter(d => d.row.id == selectedRow.id);
        }

        this.rowSelect.emit({ ...this.selection });
      }
    }
  }

  customEvent(names: string | undefined, data?: any) {
    this.custom.emit({ name: names, data: data ? { ...data } : null });
  }

  private clearDataTableInputs() {
    this.actionTypeSelect(null);
    this.actionTypeInput(null);
  }

  /**
   * This column's `aria-sort` direction. This table sorts by SEVERAL columns at once, and ARIA
   * allows the attribute on one header only, so only the PRIMARY sort answers one; a secondary
   * sort is spoken by `secondarySortRank` in the header's text.
   */
  sortDirection(columnField: TableField): SortArrowDirection {
    if (!columnField || !columnField.sortable) {
      return null;
    }
    return directionFromPrioritisedSort(this.sortStatus[columnField.id]);
  }

  /**
   * Whether any DISPLAYED column can be sorted. Under 600px the header row is the sort control and
   * is shown as a scrolling strip; a list with nothing to sort keeps it hidden, since its labels
   * are already on every card (`._isNewHeader`) and a strip of them would only repeat them.
   */
  get hasSortableColumn(): boolean {
    // Bound in the template, so read on every pass: no `displayedColumns` getter here, which maps
    // a fresh array out of `columnsFields` whenever no explicit columns are set.
    const shown = this.$displayedColumns;
    return (this.config?.columnsFields || []).some((f) => !!f?.sortable && (!shown || shown.includes(f.id)));
  }

  /**
   * The multi-sort the SERVER echoed, over the SORTABLE columns only. A default sort can name a
   * column the list displays but does not let the user sort; counted, it took position 0 and the
   * user's own sort became "secondary 2" with no header carrying aria-sort.
   */
  sortStatusFromEcho(sort: Sort[] | null | undefined): { [id: string]: SortItem } {
    return prioritisedSortFromEcho(sort, (this.config?.columnsFields || []).filter((f) => !!f?.sortable));
  }

  /** This column's rank as a SECONDARY sort (2, 3, ...), or `null`. See `sortDirection`. */
  sortRank(columnField: TableField): number | null {
    if (!columnField || !columnField.sortable) {
      return null;
    }
    return secondarySortRank(this.sortStatus[columnField.id]);
  }

  /** A secondary sort's direction, read beside `sortRank`. */
  sortRankDirection(columnField: TableField): SortArrowDirection {
    if (!columnField || !columnField.sortable) {
      return null;
    }
    return secondarySortDirection(this.sortStatus[columnField.id]);
  }

  setSort(columnField: TableField) {


    if (!columnField || !columnField.sortable) { return; }
    const sortItem: SortItem = this.sortStatus[columnField.id];
    // prima volta
    if (!sortItem) {
      this.sortStatus[columnField.id] = {
        field: columnField,
        position: Object.keys(this.sortStatus).length,
        direction: SortDirection.ASC
      };
    } else if (sortItem && sortItem.direction === SortDirection.ASC) {
      sortItem.direction = SortDirection.DESC;
    } else {
      this.sortStatus[columnField.id] = undefined as any;
      delete this.sortStatus[columnField.id];
    }
    _.sortBy(Object.values(this.sortStatus), 'position').forEach((si: SortItem, index: number) => si.position = index);
    this.sortSubject.next(this.sortStatus);
  }

  isSelectionMultiple() {
    return (this.selectMode === SelectionMode.multiple) || (this.selectMode === SelectionMode.multipleWithBaseRoute);
  }



}
